import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, interval, Observable, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { CountdownDate, CountDownRender } from './piece-countdown.model';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { getCmsConfigs, getCountryCode, getCurrentDate } from '@core/infrastructure/state/core.selectors';
import { CountDownByConfigType } from '@features/cms-render/domain/constants/cms-render.constants';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { ShowCountdown } from '@features/cms-render/domain/state/cms-render.actions';
import {
    CommonStylesRenderDirective,
} from '@features/cms-render/ui/directives/common-styles-render/common-styles-render.directive';
import { CountdownService } from '@features/cms-render/ui/services/countdown/countdown.service';

@Component({
    selector: 'piece-countdown',
    templateUrl: './piece-countdown.component.html',
    styleUrls: ['./piece-countdown.component.css'],
    providers: [CountdownService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PieceCountdownComponent extends CommonStylesRenderDirective implements OnInit, OnDestroy {
    @Input() public element: ContentItemTree;

    public isComponentReady: boolean = false;

    public textDate: string = '';

    public activeFont: string = '';

    public sizeDateEnd: string = '';

    public colorDateEnd: string = '';

    public lowerTextSize: string = '';

    public percentLeft: string = '';

    public colorProgressBar: string = '';

    public sizeProgressBar: string = '';

    public onlyHours: boolean = false;

    public hideLabels: boolean = false;

    public marginProgressBar: string = '25px 0px 0px 0px';

    private countDownSettings: CountdownDate;

    private readonly _countdownRender$: BehaviorSubject<CountDownRender> = new BehaviorSubject({
        isOnTime: true,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    public countdownRender$: Observable<CountDownRender> = this._countdownRender$.asObservable();

    public subscription: Subscription = new Subscription();

    private readonly onDestroy$ = new Subject();

    private readonly isServiceReady$ = this.countdownService.isServiceReady;

    constructor(
        private readonly countdownService: CountdownService,
        private readonly store: Store
    ) {
        super();
    }

    public ngOnInit(): void {
        this.initProperties();
        super.setDefaultHostStyles();
        this.isServiceReady();
    }

    private initProperties() {
        this.activeFont = this.element.settings?.general.activeFont;
        this.sizeDateEnd = this.element.settings?.general.sizeDateEnd;
        this.colorDateEnd = this.element.settings?.general.colorDateEnd;
        this.lowerTextSize = this.element.settings?.general.lowerTextSize;
        this.colorProgressBar = this.element.settings?.general.progressBarColor;
        this.sizeProgressBar = this.element.settings?.general.progressBarHeight;
        this.onlyHours = this.element.settings?.general.onlyHours;
        this.hideLabels = this.element.settings?.general.hideLabels;
        this.marginProgressBar = this.element.settings?.general.progressBarHeight === '0px' ? '0px' : '25px 0px 0px 0px';
    }

    public isServiceReady(): void {
        combineLatest([
            this.store.select(getCountryCode),
            this.store.select(getCurrentDate),
            this.store.select(getCmsConfigs),

        ])
            .pipe(
                filter((
                    [countryCode, currentDate, cmsConfigurations]
                ) => !!countryCode && !!currentDate && !!cmsConfigurations),
                take(1),
                takeUntil(this.onDestroy$)
            )
            .subscribe(([countryCode, currentDate, cmsConfigurations]) => {
                this.countDownSettings = this.initCountDownSettings(cmsConfigurations);
                this.countdownService.initializeService(
                    this.element.settings?.general.dateFormat,
                    this.element.settings?.general.configName,
                    countryCode,
                    currentDate,
                    this.countDownSettings
                );
                this.initCountDownIntervals();
                this.isComponentReady = true;
            });
    }

    private initCountDownSettings(cmsConfigurations) {
        const contDownSettings: CountdownDate = {
            startDate: this.element.settings.general.startDate,
            endDate: this.element.settings.general.endDate,
            fromHour: this.element.settings.general.fromHour,
            fromMinutes: this.element.settings.general.fromMinutes,
        };

        if (this.element.settings.general.dateFormat === CountDownByConfigType) {
            const config = cmsConfigurations
                .find((_config: any) => _config.key === this.element.settings.general.configName);
            if (!!config) {
                contDownSettings.endDate = config.webApp;
            }
        }
        return contDownSettings;
    }

    public initCountDownIntervals(): void {
        if (this.element.settings.general.isOnTime) {
            this.refreshCountdownRender();
            this.startIntervalCountdown();
        }
    }

    private refreshCountdownRender() {
        const duration = this.countdownService.getTimeDifference();
        this.percentLeft = `${this.countdownService.percentLeft}%`;
        this._countdownRender$.next(this.countdownService.generateCountDownRender(duration, this.onlyHours));
    }

    @BrowserOnly<PieceCountdownComponent, undefined>(undefined)
    private startIntervalCountdown() {
        this.subscription = interval(1000).pipe(
            concatLatestFrom(() => this.countdownRender$)
        ).subscribe(([_, countdownRender]) => {
            if (countdownRender.isOnTime) {
                this.refreshCountdownRender();
            } else {
                this.subscription.unsubscribe();
                this._countdownRender$.next({
                    ...countdownRender,
                    isOnTime: false,
                });
                this.store.dispatch(ShowCountdown({ showCountdown: false, slug: this.element.slug }));
            }
        });
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.onDestroy$.next(undefined);
        this.onDestroy$.complete();
    }
}
